import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Input from "@mui/material/Input";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableContainer from "@mui/material/TableContainer";
import CircularProgress from "@mui/material/CircularProgress";
import { Paginator } from "../../../shared/components/paginator";
import { useDebounce } from "../../../shared/hooks/use-debounce";
import {
	DoorRuleRequestError,
	getDoorRuleOptions,
	listDoorRules,
} from "../../../features/precificador/services/door-count-rule";
import {
	DoorRuleOption,
	DoorRuleOptions,
	DoorRuleProps,
} from "../../../features/precificador/types/door-rule";
import DoorRuleToggle, { doorRuleActiveLabel } from "./DoorRuleToggle";

const PAGE_SIZE = 20;

const EMPTY_VALUE = "—";

const LOAD_ERROR = "Não foi possível carregar as regras.";

const COLUMNS: { label: string; minWidth: number; align?: "center" }[] = [
	{ label: "Nome", minWidth: 180 },
	{ label: "Tipos de veículo", minWidth: 200 },
	{ label: "Espécie", minWidth: 120 },
	{ label: "Marca", minWidth: 140 },
	{ label: "Palavras-chave", minWidth: 160 },
	{ label: "Lugares", minWidth: 100 },
	{ label: "Portas", minWidth: 80, align: "center" },
	{ label: "Prioridade", minWidth: 100, align: "center" },
	{ label: "Ativo", minWidth: 130, align: "center" },
];

const TABLE_MIN_WIDTH = COLUMNS.reduce(
	(total, column) => total + column.minWidth,
	0,
);

const headerCellSx = {
	color: "#6B7280",
	background: "#F9FAFB",
	fontWeight: 600,
};

type ActiveFilter = "" | "true" | "false";

const parseActiveFilter = (value: string | null): ActiveFilter =>
	value === "true" || value === "false" ? value : "";

const labelOf = (list: DoorRuleOption[] | undefined, value: string) =>
	list?.find((option) => option.value === value)?.label ?? value;

const joinOrEmpty = (values: (string | number)[]) =>
	values.length ? values.join(", ") : EMPTY_VALUE;

export default function DoorRulesTab(): JSX.Element {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const searchFilter = searchParams.get("search") ?? "";
	const activeFilter = parseActiveFilter(searchParams.get("active"));
	const vehicleTypeFilter = searchParams.get("vehicle_type") ?? "";
	const brandFilter = searchParams.get("brand") ?? "";
	const [searchValue, setSearchValue] = useState<string>(searchFilter);
	const debouncedSearch = useDebounce(searchValue.trim());
	const [options, setOptions] = useState<DoorRuleOptions | null>(null);
	const [rules, setRules] = useState<DoorRuleProps[]>([]);
	const [total, setTotal] = useState<number>(0);
	const [pageNumber, setPageNumber] = useState<number>(1);
	const [loading, setLoading] = useState<boolean>(true);
	const [loadError, setLoadError] = useState<string>("");
	const requestIdRef = useRef(0);

	const vehicleTypeOptions = options?.vehicle_types ?? [];
	const vehicleTypeMenu =
		vehicleTypeFilter &&
		!vehicleTypeOptions.some((option) => option.value === vehicleTypeFilter)
			? [
					...vehicleTypeOptions,
					{ value: vehicleTypeFilter, label: vehicleTypeFilter },
				]
			: vehicleTypeOptions;

	const hasFilters =
		searchFilter !== "" ||
		activeFilter !== "" ||
		vehicleTypeFilter !== "" ||
		brandFilter !== "";

	const buildListParams = (page: number) => {
		const params = new URLSearchParams({
			page: String(page),
			page_size: String(PAGE_SIZE),
		});
		if (searchFilter) params.set("search", searchFilter);
		if (activeFilter) params.set("active", activeFilter);
		if (vehicleTypeFilter) params.set("vehicle_type", vehicleTypeFilter);
		if (brandFilter) params.set("brand", brandFilter);
		return params;
	};

	const getRules = async (page: number) => {
		const requestId = ++requestIdRef.current;
		setLoading(true);
		setLoadError("");
		try {
			const response = await listDoorRules(buildListParams(page));
			if (requestId !== requestIdRef.current) return;
			if (!response.content.length && page > 1 && response.total > 0) {
				setPageNumber(page - 1);
				getRules(page - 1);
				return;
			}
			setRules(response.content);
			setTotal(response.total);
		} catch (error) {
			if (requestId !== requestIdRef.current) return;
			setRules([]);
			setLoadError(
				error instanceof DoorRuleRequestError && error.kind === "unavailable"
					? error.message
					: LOAD_ERROR,
			);
		}
		setLoading(false);
	};

	const setFilter = (key: string, value: string) => {
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				if (value) {
					next.set(key, value);
				} else {
					next.delete(key);
				}
				return next;
			},
			{ replace: true },
		);
	};

	const handleChangePage = (
		_event: React.ChangeEvent<unknown>,
		value: number,
	) => {
		setPageNumber(value);
		getRules(value);
	};

	const handleRuleToggled = (updated: DoorRuleProps) => {
		if (activeFilter && String(updated.active) !== activeFilter) {
			getRules(pageNumber);
			return;
		}
		setRules((prev) =>
			prev.map((rule) =>
				rule.id === updated.id ? { ...rule, active: updated.active } : rule,
			),
		);
	};

	useEffect(() => {
		getDoorRuleOptions()
			.then(setOptions)
			.catch(() => setOptions(null));
	}, []);

	useEffect(() => {
		if (debouncedSearch !== searchFilter) setFilter("search", debouncedSearch);
	}, [debouncedSearch]);

	useEffect(() => {
		setPageNumber(1);
		getRules(1);
	}, [searchFilter, activeFilter, vehicleTypeFilter, brandFilter]);

	const renderFeedbackRow = (content: React.ReactNode) => (
		<TableRow>
			<TableCell colSpan={COLUMNS.length} align="center">
				{content}
			</TableCell>
		</TableRow>
	);

	return (
		<>
			<Box
				sx={{
					display: "flex",
					flexWrap: "wrap",
					gap: 2,
					alignItems: "center",
					my: 2,
				}}
			>
				<FormControl variant="standard" sx={{ width: { xs: "100%", sm: 400 } }}>
					<InputLabel htmlFor="door-rules-search">Pesquisar</InputLabel>
					<Input
						id="door-rules-search"
						placeholder="Nome ou palavra-chave"
						value={searchValue}
						onChange={(e) => setSearchValue(e.target.value)}
						startAdornment={
							<InputAdornment position="start">
								<SearchIcon fontSize="small" />
							</InputAdornment>
						}
					/>
				</FormControl>
				<FormControl variant="outlined" size="small" sx={{ minWidth: 140 }}>
					<InputLabel id="door-rules-active-label" shrink>
						Ativo
					</InputLabel>
					<Select
						labelId="door-rules-active-label"
						label="Ativo"
						value={activeFilter}
						displayEmpty
						notched
						onChange={(e) => setFilter("active", e.target.value)}
						renderValue={(value) =>
							value ? doorRuleActiveLabel(value === "true") : "Todos"
						}
					>
						<MenuItem value="">Todos</MenuItem>
						<MenuItem value="true">{doorRuleActiveLabel(true)}</MenuItem>
						<MenuItem value="false">{doorRuleActiveLabel(false)}</MenuItem>
					</Select>
				</FormControl>
				<FormControl variant="outlined" size="small" sx={{ minWidth: 180 }}>
					<InputLabel id="door-rules-vehicle-type-label" shrink>
						Tipo de veículo
					</InputLabel>
					<Select
						labelId="door-rules-vehicle-type-label"
						label="Tipo de veículo"
						value={vehicleTypeFilter}
						displayEmpty
						notched
						onChange={(e) => setFilter("vehicle_type", e.target.value)}
						renderValue={(value) =>
							value ? labelOf(options?.vehicle_types, value) : "Todos"
						}
					>
						<MenuItem value="">Todos</MenuItem>
						{vehicleTypeMenu.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<Autocomplete
					size="small"
					sx={{ width: 220 }}
					options={options?.brands ?? []}
					getOptionLabel={(option) => option.label}
					isOptionEqualToValue={(option, value) => option.value === value.value}
					value={
						brandFilter
							? {
									value: brandFilter,
									label: labelOf(options?.brands, brandFilter),
								}
							: null
					}
					onChange={(_event, option) => setFilter("brand", option?.value ?? "")}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Marca"
							placeholder="Todas"
							InputLabelProps={{ shrink: true }}
						/>
					)}
				/>
			</Box>
			<TableContainer sx={{ overflowX: "auto" }}>
				<Table
					size="small"
					aria-label="regras de número de portas"
					sx={{ minWidth: TABLE_MIN_WIDTH }}
				>
					<TableHead>
						<TableRow>
							{COLUMNS.map((column) => (
								<TableCell
									key={column.label}
									align={column.align}
									sx={{
										...headerCellSx,
										minWidth: column.minWidth,
										width: column.minWidth,
									}}
								>
									{column.label}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{loading
							? renderFeedbackRow(<CircularProgress size={24} />)
							: loadError
								? renderFeedbackRow(loadError)
								: rules.length === 0
									? renderFeedbackRow(
											hasFilters
												? "Nenhuma regra encontrada para os filtros aplicados."
												: "Nenhuma regra cadastrada",
										)
									: rules.map((rule) => (
											<TableRow
												key={rule.id}
												hover
												onClick={() => navigate(`/door-rule/${rule.id}`)}
												sx={{ cursor: "pointer" }}
											>
												<TableCell>
													<Link
														to={`/door-rule/${rule.id}`}
														onClick={(e) => e.stopPropagation()}
													>
														{rule.name}
													</Link>
												</TableCell>
												<TableCell>
													{rule.vehicle_types
														.map((type) =>
															labelOf(options?.vehicle_types, type),
														)
														.join(", ")}
												</TableCell>
												<TableCell>
													{rule.species
														? labelOf(options?.species, rule.species)
														: EMPTY_VALUE}
												</TableCell>
												<TableCell>
													{rule.brand
														? labelOf(options?.brands, rule.brand)
														: EMPTY_VALUE}
												</TableCell>
												<TableCell>{joinOrEmpty(rule.keywords)}</TableCell>
												<TableCell>{joinOrEmpty(rule.seats)}</TableCell>
												<TableCell align="center" sx={{ fontWeight: 700 }}>
													{rule.door_count}
												</TableCell>
												<TableCell align="center">{rule.priority}</TableCell>
												<TableCell align="center">
													<DoorRuleToggle
														rule={rule}
														onToggled={handleRuleToggled}
													/>
												</TableCell>
											</TableRow>
										))}
					</TableBody>
				</Table>
			</TableContainer>
			{!loadError && total > 0 && (
				<Paginator
					count={total}
					qtdResults={PAGE_SIZE}
					pageNumber={pageNumber}
					onChange={handleChangePage}
				/>
			)}
		</>
	);
}
